/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import './Ntable.css';
import clsx from 'clsx';
import _ from 'lodash';
import SortIcon from '@mui/icons-material/Sort';

export default function NTable2(props) {
  const { rows, columns, isLoading, lastElementRef } = props;

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortedRows = (_rows) => _.orderBy(_rows, [orderBy], [order]);

  const getHeader = useMemo(
    () => (
      <tr className='bg-white break:bg-nwhite-200'>
        {columns?.map((column, cIndex) => (
          <th
            width={column?.width}
            onClick={() => column?.isSortable && handleRequestSort(column?.key)}
            key={`${column.key}column${cIndex}`}
            className={clsx(
              'whitespace-nowrap px-2 break:px-4 break:first:pl-5 break:last:pr-5 last:macmd:pr-8 first:maclg:pl-8',
              `sticky -top-1 z-10 bg-white py-3 text-left text-sm font-semibold break:bg-nwhite-200 break:py-4 break:text-base maclg:py-7 maclg:text-xl`,
              { 'cursor-pointer': column?.isSortable },
            )}
          >
            <div className={`${column?.columClassName} flex items-center gap-0.5`}>
              {typeof column?.headerRender === 'function' ? column?.headerRender() : column.title}{' '}
              {column?.isSortable && (
                <SortIcon
                  className={clsx('text-base', {
                    'rotate-180': column?.key === orderBy && order === 'asc',
                  })}
                />
              )}
            </div>
          </th>
        ))}
      </tr>
    ),
    [rows.length, orderBy, order, columns],
  );

  const getRows = useMemo(
    () =>
      rows?.length > 0 &&
      getSortedRows(rows)?.map((row, rIndex) => (
        <tr
          ref={rows.length === rIndex + 1 ? lastElementRef : null}
          key={`tr-${rIndex}`}
          className={clsx('', { 'bg-white break:bg-nwhite-300': (rIndex + 1) % 2 === 0 })}
        >
          {columns?.map((column, cIndex) => (
            <td
              width={column?.width}
              className={clsx(
                'px-2 break:px-4 break:first:pl-5 break:last:pr-5 last:macmd:pr-8 first:maclg:pl-8',
                `${column?.rowClassName}  py-1 text-xs break:py-2 break:text-sm maclg:py-3 maclg:text-lg`,
              )}
              key={`${column?.key}${cIndex}`}
            >
              {typeof column?.rowRender === 'function' ? column.rowRender(row, rIndex) : ''}{' '}
            </td>
          ))}
        </tr>
      )),
    [rows.length, orderBy, order, columns],
  );

  return (
    <div
      className={clsx(
        { 'App-Ntable2 mb-1 h-full overflow-auto rounded-[10px]': !isLoading || rows?.length },
        { 'relative min-h-[200px]': !isLoading && !rows?.length },
      )}
    >
      {isLoading && !rows?.length ? (
        <div className='mt-10 flex h-full items-center justify-center text-center'>
          <CircularProgress />
        </div>
      ) : (
        <>
          <table className='h-full w-full'>
            <thead className=''>{getHeader}</thead>
            <tbody>{getRows}</tbody>
          </table>
          {!isLoading && !rows?.length && (
            <div className='absolute top-[70%] w-full text-center'>No records!</div>
          )}
          {isLoading && <div className='my-4 flex justify-center font-semibold'>Loading...</div>}
        </>
      )}
    </div>
  );
}

NTable2.propTypes = {
  isLoading: PropTypes.bool,
  lastElementRef: PropTypes.any,
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      width: PropTypes.string,
      columClassName: PropTypes.string,
      rowClassName: PropTypes.string,
      isSortable: PropTypes.bool,
      title: PropTypes.string,
      rowRender: PropTypes.func,
      headerRender: PropTypes.func,
    }),
  ),
};

NTable2.defaultProps = {
  lastElementRef: undefined,
  isLoading: false,
  columns: [],
  rows: [],
};
